import { useState } from 'react';
import { Text, Button, Dialog, Spinner } from 'frosted-ui';
import UpgradeButton from './UpgradeButton';
import { usePlan, waitForProAccess } from '../../hooks/usePlan';
import { upgradeBody, WHOP_MEMBERSHIPS_URL } from './plan';

interface UpgradeDialogProps {
  companyId: string;
  onClose: () => void;
  /** Called once Pro is active (our webhook has landed). */
  onUpgraded?: () => void;
}

/** The upgrade prompt, opened when a seller past their free test starts another. */
export default function UpgradeDialog({ companyId, onClose, onUpgraded }: UpgradeDialogProps) {
  const [activating, setActivating] = useState(false);
  const [stillPending, setStillPending] = useState(false);
  const { plan, loading } = usePlan(companyId);
  // Their subscription still exists: a new checkout would mean paying twice.
  const pastDue = plan?.status === 'past_due';

  async function handlePaid() {
    setActivating(true);
    setStillPending(false);
    const active = await waitForProAccess(companyId);
    setActivating(false);
    if (active) onUpgraded?.();
    else setStillPending(true);
  }

  return (
    <Dialog.Root open onOpenChange={(open: boolean) => !open && !activating && onClose()}>
      <Dialog.Content style={{ maxWidth: 480 }}>
        <Dialog.Title size="5" style={{ marginBottom: 'var(--space-2)' }}>
          {pastDue ? 'Your last Pro payment failed' : 'Run your next test with Pro'}
        </Dialog.Title>
        {loading ? (
          <Spinner size="2" aria-label="Loading your plan" />
        ) : (
          <>
            <Text size="2" color="gray" style={{ display: 'block', marginBottom: 'var(--space-4)' }}>
              {pastDue
                ? 'Update your card on Whop to keep Pro. Anything you have running keeps running.'
                : upgradeBody(plan?.trialAvailable)}
            </Text>
            {stillPending ? (
              // Paid, and waiting on Whop's word: another checkout could mean paying twice.
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
                <Text size="2" color="gray" role="status">
                  Payment received — Pro can take a minute to switch on. Close this and try again shortly.
                </Text>
                <Button size="3" variant="soft" color="gray" onClick={onClose}>
                  Close
                </Button>
              </div>
            ) : activating ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }} role="status">
                <Spinner size="2" />
                <Text size="2" color="gray">Activating Pro…</Text>
              </div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
                {pastDue ? (
                  <Button size="3" variant="solid" asChild>
                    <a href={WHOP_MEMBERSHIPS_URL} target="_blank" rel="noopener noreferrer">
                      Update card on Whop
                    </a>
                  </Button>
                ) : (
                  <UpgradeButton companyId={companyId} trialAvailable={plan?.trialAvailable} onSuccess={handlePaid} />
                )}
                <Button size="3" variant="ghost" color="gray" onClick={onClose}>
                  Not now
                </Button>
              </div>
            )}
          </>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
}
